import React from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow, SectionTitle, Lead } from "../components/Primitives";
import { ArrowRight, Heart, BookOpen, Home as HomeIcon, HandHeart, Users, Clock } from "lucide-react";

const hero = "https://images.unsplash.com/photo-1622506092974-38b108ee3436?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxidWRkaGlzdCUyMHRlbXBsZXxlbnwwfHx8fDE3ODgyNDUxMTR8MA&ixlib=rb-4.1.0&q=85";
const meditation = "https://images.unsplash.com/photo-1558868540-3b5e8ca26dc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxtZWRpdGF0aW9uJTIwbW9ua3N8ZW58MHx8fHwxNzg4MjQ1MTE0fDA&ixlib=rb-4.1.0&q=85";
const community = "https://images.pexels.com/photos/27471164/pexels-photo-27471164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Home = () => {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#f6efe0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 rise">
            <span className="sep-lotus mb-8">Van Hanh Monastery · Est. 1983</span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#3d2817]">
              A quiet place<br />for every open heart.
            </h1>
            <p className="mt-8 text-lg text-[#5a4a3a] leading-relaxed max-w-xl">
              For over forty years, our doors in Lyneham have been open to anyone seeking stillness, learning, kindness or a warm meal. Whatever brings you here, you are welcome.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/visit" className="btn-primary">
                Plan Your Visit <ArrowRight size={18} />
              </Link>
              <Link to="/programs" className="btn-outline">
                Explore Our Programs
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: "40+", l: "Years of service" },
                { n: "12", l: "Community programs" },
                { n: "All", l: "Are welcomed" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl text-[#7a2e1f]">{s.n}</div>
                  <div className="text-xs uppercase tracking-widest text-[#8a6b3f] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative rise rise-delay-2">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_30px_60px_-30px_rgba(61,40,23,0.5)]">
              <img
                src={hero}
                alt="Golden Buddhist temple bathed in warm light"
                className="w-full h-full object-cover"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3d2817]/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#fdfaf3] p-6 max-w-xs shadow-[0_20px_40px_-20px_rgba(61,40,23,0.35)] hidden md:block">
              <p className="font-serif text-xl text-[#3d2817] leading-snug">“Peace comes from within. Do not seek it without.”</p>
              <p className="mt-2 text-xs uppercase tracking-widest text-[#8a6b3f]">— The Buddha</p>
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Eyebrow>Welcome</Eyebrow>
            <SectionTitle>A home for compassion in the heart of Canberra.</SectionTitle>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#4a3a2a] text-[17px] leading-relaxed">
            <p>
              Van Hanh Monastery is a place where the practice of Buddhism meets the everyday needs of people in our community. Founded in 1983 by the Vietnamese Buddhist community and now a registered Australian charity, we offer meditation, dharma teaching, cultural events, and quiet friendship to anyone who walks through our gates.
            </p>
            <p>
              We also do the ordinary, essential work of care — warm meals for those doing it tough, safe budget lodging, aged support, language classes, family counselling, and a listening ear when it’s needed most.
            </p>
            <p className="italic text-[#7a2e1f] font-serif text-xl">You do not need to be Buddhist. You do not need to bring anything. Just come as you are.</p>
          </div>
        </div>
      </Section>

      {/* PILLARS */}
      <section className="bg-[#f6efe0] border-y border-[#e9dcc0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="text-center mb-16">
            <Eyebrow>What we do</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl text-[#3d2817] mt-6">Four ways we serve, every week.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { Icon: BookOpen, t: "Teaching & Practice", d: "Meditation sits, dharma talks, chanting and quiet study — open to all levels." },
              { Icon: HomeIcon, t: "Shelter & Housing", d: "Rahula Community Lodge offers safe, low-cost accommodation for those in need." },
              { Icon: HandHeart, t: "Relief & Care", d: "Emergency aid, vegetarian meals, counselling and support for families in crisis." },
              { Icon: Users, t: "Cultural Belonging", d: "Vietnamese language, festival celebrations and youth groups keeping heritage alive." },
            ].map(({ Icon, t, d }, i) => (
              <div key={t} className="bg-[#fdfaf3] p-8 border border-[#e9dcc0] hover:border-[#c9a15b] transition-colors duration-300">
                <Icon size={28} className="text-[#7a2e1f]" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-[#3d2817] mt-5 mb-3">{t}</h3>
                <p className="text-[15px] text-[#5a4a3a] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE + TEXT */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative aspect-[5/6] overflow-hidden">
            <img src={meditation} alt="Practitioner in meditation at Van Hanh Monastery" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div>
            <Eyebrow>Weekly meditation</Eyebrow>
            <SectionTitle>Sit with us. There is nothing to prepare.</SectionTitle>
            <p className="mt-6 text-[17px] text-[#4a3a2a] leading-relaxed">
              Our weekly meditation sessions are open, gentle and free. Bring what you have; leave what you don’t need. Guidance is offered in English and Vietnamese, and every session ends with a simple cup of tea.
            </p>
            <ul className="mt-8 space-y-3 text-[#3d2817]">
              {[
                { day: "Thursday", time: "7:30 pm", note: "Evening meditation · English guidance" },
                { day: "Sunday", time: "6:30 pm", note: "Community sit · Vietnamese & English" },
                { day: "Sunday", time: "10:00 am", note: "Dharma talk & family gathering" },
              ].map((row) => (
                <li key={row.day + row.time} className="flex items-start gap-4 py-3 border-b border-[#e9dcc0]">
                  <Clock size={18} className="text-[#c9a15b] mt-1" />
                  <div>
                    <div className="font-serif text-xl">{row.day} · <span className="text-[#7a2e1f]">{row.time}</span></div>
                    <div className="text-sm text-[#5a4a3a]">{row.note}</div>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/visit" className="btn-outline mt-8">Plan your first visit</Link>
          </div>
        </div>
      </Section>

      {/* QUOTE / COMMUNITY */}
      <section className="relative bg-[#7a2e1f] text-[#f6efe0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
          <span className="sep-lotus text-[#c9a15b] justify-center mb-8" style={{ color: "#c9a15b" }}>A shared bowl</span>
          <p className="font-serif text-3xl md:text-4xl leading-[1.25]">
            “We were welcomed like family the very first time we walked in. The monks remembered our children’s names by the second visit. That kindness is what keeps us coming back.”
          </p>
          <p className="mt-8 text-sm uppercase tracking-[0.3em] text-[#c9a15b]">Anh · Canberra parent</p>
        </div>
      </section>

      {/* OUTREACH */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow>Community outreach</Eyebrow>
            <SectionTitle>Care that reaches beyond our gates.</SectionTitle>
            <p className="mt-6 text-[17px] text-[#4a3a2a] leading-relaxed">
              We partner with local health providers, refugee support networks and rural communities — offering in-language health workshops, hepatitis awareness, food relief, and quiet company for those recovering from disaster or loss.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { n: "3,200+", l: "Meals shared each year" },
                { n: "48", l: "Nights of shelter monthly" },
                { n: "14", l: "Community partners" },
                { n: "9", l: "Languages spoken here" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#c9a15b] pl-4">
                  <div className="font-serif text-3xl text-[#7a2e1f]">{s.n}</div>
                  <div className="text-sm text-[#5a4a3a]">{s.l}</div>
                </div>
              ))}
            </div>
            <Link to="/community" className="btn-outline mt-10">See our outreach work</Link>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={community} alt="Community members sharing a meal together" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-[#f6efe0] border-t border-[#e9dcc0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <Heart size={32} className="text-[#7a2e1f] mx-auto" strokeWidth={1.5} />
          <h2 className="font-serif text-4xl md:text-5xl text-[#3d2817] mt-6">Small gifts, quiet miracles.</h2>
          <p className="mt-6 text-lg text-[#5a4a3a] max-w-2xl mx-auto leading-relaxed">
            Every donation — whether it feeds a family, keeps a lamp lit, or shelters someone for a night — is received with deep gratitude and used with care.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/support" className="btn-primary">Ways to give <ArrowRight size={18} /></Link>
            <Link to="/contact" className="btn-outline">Contact our office</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
